import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Images, Loader2, Pencil, TriangleAlert } from 'lucide-react';
import { construirDocumento, construirPaleta, validarConfig } from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, urlDaImagem, type MioloCompleto } from '@/lib/api';
import { AVISO_BONUS_REVOGADO, semCadernosDeBonus } from '@/lib/bonus';
import { useAuth } from '@/auth/AuthContext';
import { useAcesso } from '@/auth/EstadoAcesso';
import { useImpressao } from '@/impressao/useImpressao';
import { useFilaDoPdf } from '@/impressao/useFilaDoPdf';
import { DialogoMockup } from '@/mockup/DialogoMockup';
import { PainelPrevia } from '@/wizard/PainelPrevia';

export function VerMiolo() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const { usuario } = useAuth();
  const [miolo, setMiolo] = useState<MioloCompleto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [duplicando, setDuplicando] = useState(false);
  const [mockupAberto, setMockupAberto] = useState(false);
  const fila = useFilaDoPdf({ mioloId: id ?? null, nome: miolo?.nome ?? 'miolo' });

  useEffect(() => {
    if (!id) return;
    let ativo = true;
    api
      .obterMiolo(id)
      .then((resposta) => {
        if (ativo) setMiolo(resposta.miolo);
      })
      .catch((falha) => {
        if (ativo) setErro(falha instanceof Error ? falha.message : 'Não foi possível abrir o miolo.');
      });
    return () => {
      ativo = false;
    };
  }, [id]);

  /**
   * Pede o PDF ao servidor.
   *
   * O arquivo é desenhado pelo Chromium do servidor (a mesma página de impressão
   * do site), então sai igual para qualquer navegador, no tamanho exato da folha.
   * Quem espera é o servidor: aqui a tela fica livre, e o download começa sozinho
   * quando o arquivo fica pronto ([[fila de renderização]]).
   */
  const baixarPdf = () => {
    if (!id || !miolo) return;
    setErro(null);
    void fila.pedir();
  };

  /** A biblioteca de prompts é um bônus: sem ele o caderno nem é montado. */
  const temBonus = Boolean(usuario?.promptsIA || usuario?.admin);

  const config = useMemo(() => {
    if (!miolo) return null;
    const validada = validarConfig(miolo.config).config ?? null;
    return validada ? semCadernosDeBonus(validada, temBonus) : null;
  }, [miolo, temBonus]);

  const bonusRemovido = useMemo(
    () =>
      Boolean(
        miolo &&
          !temBonus &&
          (validarConfig(miolo.config).config?.cadernos ?? []).some((caderno) => caderno.tipo === 'PROMPTS'),
      ),
    [miolo, temBonus],
  );

  const paleta = useMemo(() => (config ? construirPaleta(config.cores) : null), [config]);
  const documento = useMemo(() => (config ? construirDocumento(config) : null), [config]);

  const { marcaDagua, textoMarcaDagua, detalheMarcaDagua, bloqueado } = useAcesso();

  const { imprimir, imprimindo, areaImpressao } = useImpressao({
    documento: documento ?? { paginas: [], resumo: null as never, avisos: [] },
    paleta: paleta ?? ({} as never),
    config: config ?? ({} as never),
    resolverImagem: urlDaImagem,
    marcaDagua: marcaDagua ? textoMarcaDagua : null,
    marcaDaguaDetalhe: detalheMarcaDagua,
  });

  const duplicar = async () => {
    if (!id) return;
    setDuplicando(true);    try {
      const resposta = await api.duplicarMiolo(id);
      navegar(`/miolos/${resposta.miolo.id}`);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível duplicar.');
    } finally {
      setDuplicando(false);    }
  };

  if (erro) {
    return (
      <div className="mx-auto max-w-lg space-y-3 py-16 text-center">
        <TriangleAlert className="mx-auto size-6 text-destructive" />
        <p className="text-sm text-muted-foreground">{erro}</p>
        <Button asChild variant="contorno">
          <Link to="/meus-miolos">Voltar para meus miolos</Link>
        </Button>
      </div>
    );
  }

  if (!miolo || !config || !paleta || !documento) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        <span className="text-sm">Abrindo o miolo...</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {areaImpressao}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button asChild variant="fantasma" tamanho="icone" aria-label="Voltar">
            <Link to="/meus-miolos">
              <ArrowLeft />
            </Link>
          </Button>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{miolo.nome}</p>
            <p className="truncate text-xs text-muted-foreground">
              {documento.resumo.formato} ·{' '}
              {documento.resumo.fimDeSemanaDividido
                ? '1 dia por página, com o fim de semana dividido'
                : `${documento.resumo.diasPorPagina} dia(s) por página`}{' '}
              · {documento.resumo.periodo}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tom="principal">{documento.resumo.totalPaginas} páginas</Badge>
          <Button
            variant="contorno"
            onClick={() => void duplicar()}
            title={bloqueado ? 'Acesso bloqueado: não é possível duplicar.' : undefined}
            disabled={duplicando || bloqueado}
          >
            {duplicando ? <Loader2 className="animate-spin" /> : <Copy />}
            Duplicar
          </Button>
          <Button asChild variant="contorno">
            <Link to={`/miolos/${miolo.id}/editar`}>
              <Pencil />
              Editar
            </Link>
          </Button>
          <Button
            variant="contorno"
            onClick={() => setMockupAberto(true)}
            title="Foto da agenda aberta para publicar em rede social."
          >
            <Images />
            Imagem de divulgação
          </Button>
        </div>
      </div>

      {mockupAberto && config && paleta && documento ? (
        <DialogoMockup
          aberto={mockupAberto}
          aoFechar={() => setMockupAberto(false)}
          mioloId={miolo.id}
          nomeDoMiolo={miolo.nome}
          assinaturaSugerida={usuario?.nome ?? miolo.nome}
          config={config}
          paleta={paleta}
          documento={documento}
        />
      ) : null}

      {bonusRemovido ? (
        <p className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          {AVISO_BONUS_REVOGADO}
        </p>
      ) : null}

      <div className="mx-auto max-w-md lg:max-w-lg">
        <div className="rounded-lg border border-border bg-card p-4">
          <PainelPrevia
            config={config}
            paleta={paleta}
            documento={documento}
            aoImprimir={imprimir}
            imprimindo={imprimindo}
            aoBaixarPdf={baixarPdf}
            gerandoPdf={fila.ocupado}
            avisoPdf={fila.aviso}
          />
        </div>
      </div>
    </div>
  );
}
